import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaArrowRight, 
  FaCode, 
  FaMobile, 
  FaVrCardboard, 
  FaBrain, 
  FaRocket, 
  FaShieldAlt 
} from 'react-icons/fa';

// Dynamic Hero Section with Floating Elements
const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: 
    linear-gradient(135deg, #ffffff 0%, #f8f9ff 15%, #ffffff 30%, #f5f7ff 45%, #ffffff 60%, #f0f4ff 75%, #ffffff 100%),
    radial-gradient(circle at 10% 80%, rgba(0, 122, 255, 0.07) 0%, transparent 50%),
    radial-gradient(circle at 90% 20%, rgba(255, 59, 48, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 30% 30%, rgba(48, 209, 88, 0.04) 0%, transparent 50%),
    radial-gradient(circle at 70% 70%, rgba(255, 204, 0, 0.03) 0%, transparent 50%);
  perspective: 1200px;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(45deg, rgba(0, 122, 255, 0.03) 1px, transparent 1px),
      linear-gradient(-45deg, rgba(0, 122, 255, 0.03) 1px, transparent 1px);
    background-size: 40px 40px;
    opacity: 0.6;
    animation: diagonalFlow 20s linear infinite;
  }
  
  @keyframes diagonalFlow {
    0% { transform: translate(0, 0) rotate(0deg); }
    100% { transform: translate(40px, 40px) rotate(2deg); }
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;

const MorphingShape = styled(motion.div)`
  position: absolute;
  background: ${props => props.gradient};
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: ${props => props.radius || '40px'};
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.08),
    inset 0 2px 0 rgba(255, 255, 255, 0.6);
`;

const PulsingOrb = styled(motion.div)`
  position: absolute;
  background: ${props => props.gradient};
  border-radius: 50%;
  filter: blur(2px);
  opacity: 0.6;
`;

const WavePattern = styled(motion.div)`
  position: absolute;
  background: ${props => props.pattern};
  border-radius: 50%;
  opacity: 0.4;
`;

const HeroBackground = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 90%, rgba(0, 122, 255, 0.08) 0%, transparent 40%),
    radial-gradient(circle at 80% 10%, rgba(255, 59, 48, 0.06) 0%, transparent 40%),
    radial-gradient(circle at 40% 50%, rgba(48, 209, 88, 0.05) 0%, transparent 40%),
    radial-gradient(circle at 90% 80%, rgba(88, 86, 214, 0.04) 0%, transparent 40%);
`;

const HeroContainer = styled.div`
  max-width: 980px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  text-align: center;
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(3rem, 7vw, 6rem);
  font-weight: 900;
  margin-bottom: ${props => props.theme.spacing.lg};
  letter-spacing: -0.05em;
  line-height: 1.05;
  background: linear-gradient(135deg, 
    #1a1a1a 0%, 
    #007AFF 15%, 
    #5856D6 30%, 
    #FF3B30 45%, 
    #30D158 60%, 
    #FFD60A 75%, 
    #1a1a1a 100%);
  background-size: 600% 600%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: epicGradientFlow 15s ease infinite;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  @keyframes epicGradientFlow {
    0%, 100% { background-position: 0% 50%; }
    14% { background-position: 100% 0%; }
    28% { background-position: 100% 50%; }
    42% { background-position: 100% 100%; }
    56% { background-position: 50% 100%; }
    70% { background-position: 0% 100%; }
    84% { background-position: 0% 50%; }
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: rgba(26, 26, 26, 0.8);
  margin-bottom: ${props => props.theme.spacing.xxl};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  font-weight: 400;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(motion.button)`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.radius.medium};
  font-weight: 500;
  font-size: 1.1rem;
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  box-shadow: ${props => props.theme.shadows.medium};
  border: none;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.large};
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: translateX(2px);
  }
`;

const SecondaryButton = styled(motion.button)`
  background: transparent;
  color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radius.medium};
  font-weight: 500;
  font-size: 1.1rem;
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  cursor: pointer;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(0, 122, 255, 0.05);
  }
`;

// Services Section
const ServicesSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: ${props => props.theme.colors.background};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxxl};
`;

const SectionTitle = styled(motion.h2)`
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 ${props => props.theme.spacing.md};
  }
`;

const ServiceCard = styled(motion.div)`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radius.large};
  padding: ${props => props.theme.spacing.xl};
  transition: ${props => props.theme.transition};
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.large};
    border-color: rgba(0, 122, 255, 0.3);
  }
`;

const ServiceIcon = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #5856D6);
  border-radius: ${props => props.theme.radius.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  
  svg {
    font-size: 1.5rem;
    color: white;
  }
`;

const ServiceTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: 1.4rem;
`;

const ServiceDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ServiceLink = styled.div`
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: translateX(2px);
  }
`;

// CTA Section
const CtaSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: ${props => props.theme.colors.backgroundDark};
  color: white;
  text-align: center;
`;

const CtaContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const CtaTitle = styled(motion.h2)`
  color: white;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CtaDescription = styled(motion.p)`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: ${props => props.theme.spacing.xxl};
  line-height: 1.6;
`;

const CtaButton = styled(motion.button)`
  background: white;
  color: ${props => props.theme.colors.text};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.radius.medium};
  font-weight: 600;
  font-size: 1.1rem;
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  border: none;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.theme.colors.backgroundAlt};
    transform: translateY(-1px);
  }
`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const Home = ({ setCursorVariant }) => {
  useEffect(() => {
    document.title = "D-Soft | Soluzioni Software Moderne";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: FaCode,
      title: "Sviluppo Web",
      description: "Siti web moderni e responsive costruiti con le tecnologie più avanzate per performance eccezionali.",
      color1: "#007AFF",
      color2: "#5856D6",
      link: "/servizio/sviluppo-web"
    },
    {
      icon: FaMobile,
      title: "App Mobile",
      description: "Applicazioni native e cross-platform per iOS e Android con esperienza utente eccellente.",
      color1: "#30D158",
      color2: "#32D74B",
      link: "/servizio/app-mobile"
    },
    {
      icon: FaVrCardboard,
      title: "Realtà Virtuale e Aumentata",
      description: "Esperienze immersive VR/AR che ridefiniscono l'interazione con il digitale.",
      color1: "#FF3B30",
      color2: "#FF9500",
      link: "/servizio/realta-virtuale"
    },
    {
      icon: FaBrain,
      title: "Intelligenza Artificiale",
      description: "Soluzioni AI avanzate per automatizzare processi e migliorare l'efficienza aziendale.",
      color1: "#FFD60A",
      color2: "#FFCC02",
      link: "/servizio/intelligenza-artificiale"
    }
  ];

  return (
    <>
      <HeroSection>
        <HeroBackground />
        
        <FloatingElements>
          {/* Large Morphing Shapes */}
          <MorphingShape
            gradient="linear-gradient(135deg, rgba(0, 122, 255, 0.2) 0%, rgba(88, 86, 214, 0.1) 100%)"
            radius="60px"
            animate={{
              x: [0, 200, -150, 0],
              y: [0, -100, 80, 0],
              rotate: [0, 180, 360],
              scale: [1, 1.3, 0.7, 1],
              borderRadius: ["60px", "30px", "80px", "60px"],
            }}
            transition={{
              duration: 40,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '300px',
              height: '300px',
              top: '8%',
              left: '5%',
            }}
          />
          
          <MorphingShape
            gradient="linear-gradient(135deg, rgba(255, 59, 48, 0.18) 0%, rgba(255, 149, 0, 0.09) 100%)"
            radius="50%"
            animate={{
              x: [0, -180, 140, 0],
              y: [0, 120, -90, 0],
              rotate: [0, -240, -480],
              scale: [1, 0.8, 1.4, 1],
            }}
            transition={{
              duration: 45,
              repeat: Infinity,
              ease: "linear",
            }}
            style={{
              width: '250px',
              height: '250px',
              top: '65%',
              right: '8%',
            }}
          />

          {/* Pulsing Orbs */}
          {[...Array(8)].map((_, i) => (
            <PulsingOrb
              key={i}
              gradient={`radial-gradient(circle, 
                rgba(${['0, 122, 255', '255, 59, 48', '48, 209, 88', '255, 204, 0', '88, 86, 214', '255, 149, 0', '50, 215, 75', '255, 214, 10'][i]}, 0.3) 0%, 
                rgba(${['88, 86, 214', '255, 149, 0', '50, 215, 75', '255, 214, 10', '0, 122, 255', '255, 59, 48', '48, 209, 88', '255, 204, 0'][i]}, 0.1) 100%)`}
              animate={{
                x: [0, 80 + i * 15, -40 - i * 10, 0],
                y: [0, -60 - i * 12, 80 + i * 8, 0],
                scale: [1, 1.5, 0.5, 1],
                opacity: [0.6, 1, 0.3, 0.6],
              }}
              transition={{
                duration: 25 + i * 5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.8,
              }}
              style={{
                width: `${60 + i * 20}px`,
                height: `${60 + i * 20}px`,
                top: `${10 + i * 10}%`,
                left: `${8 + i * 12}%`,
              }}
            />
          ))}

          {/* Wave Patterns */}
          <WavePattern
            pattern="conic-gradient(from 0deg, 
              rgba(0, 122, 255, 0.1) 0deg,
              rgba(255, 59, 48, 0.08) 72deg,
              rgba(48, 209, 88, 0.09) 144deg,
              rgba(255, 204, 0, 0.07) 216deg,
              rgba(88, 86, 214, 0.08) 288deg,
              rgba(0, 122, 255, 0.1) 360deg)"
            animate={{
              rotate: [0, 360],
              scale: [1, 1.2, 0.8, 1],
              x: [0, 60, -40, 0],
              y: [0, -30, 50, 0],
            }}
            transition={{
              duration: 50,
              repeat: Infinity,
              ease: "linear",
            }}
            style={{
              width: '400px',
              height: '400px',
              top: '20%',
              right: '10%',
            }}
          />

          {/* Floating Geometric Elements */}
          <motion.div
            style={{
              position: 'absolute',
              width: '500px',
              height: '120px',
              top: '35%',
              left: '3%',
              background: `
                repeating-linear-gradient(45deg,
                  rgba(0, 122, 255, 0.05) 0px,
                  rgba(0, 122, 255, 0.02) 15px,
                  transparent 30px,
                  rgba(255, 59, 48, 0.04) 45px,
                  transparent 60px,
                  rgba(48, 209, 88, 0.03) 75px,
                  transparent 90px)
              `,
              borderRadius: '60px',
              opacity: 0.8,
            }}
            animate={{
              x: [0, 80, -50, 0],
              rotateZ: [0, 5, -3, 0],
              scaleY: [1, 1.3, 0.7, 1],
              skewX: [0, 10, -5, 0],
            }}
            transition={{
              duration: 35,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Morphing Hexagon */}
          <motion.div
            style={{
              position: 'absolute',
              width: '200px',
              height: '200px',
              top: '10%',
              right: '30%',
              background: 'linear-gradient(135deg, rgba(88, 86, 214, 0.15) 0%, rgba(0, 122, 255, 0.08) 100%)',
              clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              backdropFilter: 'blur(15px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
            animate={{
              rotate: [0, 120, 240, 360],
              scale: [1, 1.4, 0.6, 1],
              x: [0, 70, -35, 0],
              y: [0, -50, 80, 0],
              clipPath: [
                'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
                'polygon(25% 25%, 75% 25%, 75% 75%, 25% 75%)',
                'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              ],
            }}
            transition={{
              duration: 30,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </FloatingElements>

        <HeroContainer>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <HeroTitle>
              Trasformiamo Idee
              <br />
              in Soluzioni Digitali
            </HeroTitle>
            
            <HeroSubtitle>
              Creiamo software innovativo che accelera la crescita aziendale. 
              Dalla concezione alla realizzazione, trasformiamo le tue visioni in realtà digitali straordinarie.
            </HeroSubtitle>
            
            <HeroButtons variants={fadeInUp}>
              <Link to="/servizi">
                <PrimaryButton
                  onMouseEnter={() => setCursorVariant("button")}
                  onMouseLeave={() => setCursorVariant("default")}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Esplora i Servizi
                  <FaArrowRight />
                </PrimaryButton>
              </Link>
              
              <Link to="/portfolio">
                <SecondaryButton
                  onMouseEnter={() => setCursorVariant("button")}
                  onMouseLeave={() => setCursorVariant("default")}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Vedi i Nostri Progetti
                </SecondaryButton>
              </Link>
            </HeroButtons>
          </motion.div>
        </HeroContainer>
      </HeroSection>

      <ServicesSection>
        <div className="container">
          <SectionHeader>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerContainer}
            >
              <SectionTitle 
                variants={fadeInUp}
                onMouseEnter={() => setCursorVariant("text")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                Tutto quello che serve per il successo
              </SectionTitle>
              
              <SectionSubtitle variants={fadeInUp}>
                Dal concept al deployment, forniamo soluzioni complete 
                che guidano la crescita e l'innovazione per aziende di ogni dimensione.
              </SectionSubtitle>
            </motion.div>
          </SectionHeader>

          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setCursorVariant("link")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                <ServiceIcon>
                  <service.icon />
                </ServiceIcon>
                
                <ServiceTitle>{service.title}</ServiceTitle>
                
                <ServiceDescription>
                  {service.description}
                </ServiceDescription>
                
                <Link to={service.link}>
                  <ServiceLink>
                    Scopri di più
                    <FaArrowRight />
                  </ServiceLink>
                </Link>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </div>
      </ServicesSection>

      <CtaSection>
        <CtaContainer>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <CtaTitle 
              variants={fadeInUp}
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              Pronto per iniziare?
            </CtaTitle>
            
            <CtaDescription variants={fadeInUp}>
              Lavoriamo insieme per dare vita alla tua visione. 
              Il nostro team è pronto ad aiutarti a costruire qualcosa di straordinario.
            </CtaDescription>
            
            <motion.div variants={fadeInUp}>
              <Link to="/contatti">
                <CtaButton
                  onMouseEnter={() => setCursorVariant("button")}
                  onMouseLeave={() => setCursorVariant("default")}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Inizia il tuo progetto
                  <FaArrowRight />
                </CtaButton>
              </Link>
            </motion.div>
          </motion.div>
        </CtaContainer>
      </CtaSection>
    </>
  );
};

export default Home; 